/**
 * DatePickerUS — date input that always renders MM/DD/YYYY and stores the
 * canonical ISO YYYY-MM-DD string under the hood. Uses shadcn Popover +
 * Calendar so the visual format does not depend on the browser locale.
 *
 *  <DatePickerUS
 *    value="2026-03-15"        // ISO YYYY-MM-DD or ""
 *    onChange={(iso) => ...}   // receives ISO YYYY-MM-DD or ""
 *    min="2026-01-01"          // optional ISO lower bound
 *    testId="contract-start"
 *  />
 */
import React from "react";
import { format, parse, parseISO, isValid } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";

function isoToDate(iso) {
  if (!iso) return null;
  try {
    const d = parseISO(iso);
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
}

function dateToISO(d) {
  if (!d || !isValid(d)) return "";
  return format(d, "yyyy-MM-dd");
}

function fmtUS(d) {
  if (!d || !isValid(d)) return "";
  return format(d, "MM/dd/yyyy");
}

export function DatePickerUS({
  value,
  onChange,
  min,
  max,
  testId,
  placeholder = "MM/DD/YYYY",
  disabled = false,
  clearable = true,
}) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(fmtUS(isoToDate(value)));

  // Keep visible text in sync when the parent updates value (e.g. via reset).
  React.useEffect(() => {
    setDraft(fmtUS(isoToDate(value)));
  }, [value]);

  const minDate = isoToDate(min);
  const maxDate = isoToDate(max);
  const selected = isoToDate(value);

  function commitDraft(text) {
    const trimmed = (text || "").trim();
    if (!trimmed) {
      onChange("");
      setDraft("");
      return;
    }
    // Accept MM/DD/YYYY (and M/D/YYYY)
    const parsed = parse(trimmed, "MM/dd/yyyy", new Date());
    if (isValid(parsed)) {
      const iso = dateToISO(parsed);
      onChange(iso);
      setDraft(fmtUS(parsed));
      return;
    }
    const loose = parse(trimmed, "M/d/yyyy", new Date());
    if (isValid(loose)) {
      const iso = dateToISO(loose);
      onChange(iso);
      setDraft(fmtUS(loose));
      return;
    }
    // Invalid input → revert to last good value
    setDraft(fmtUS(isoToDate(value)));
  }

  function onTextChange(e) {
    // Auto-insert "/" while typing
    let v = e.target.value.replace(/[^0-9/]/g, "");
    if (v.length === 2 && draft.length === 1) v += "/";
    else if (v.length === 5 && draft.length === 4) v += "/";
    if (v.length > 10) v = v.slice(0, 10);
    setDraft(v);
  }

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={onTextChange}
        onBlur={(e) => commitDraft(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        className="cm-input pr-16"
      />
      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
        {clearable && draft && !disabled && (
          <button
            type="button"
            onClick={() => {
              setDraft("");
              onChange("");
            }}
            data-testid={testId ? `${testId}-clear` : undefined}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            aria-label="Limpar data"
          >
            <X size={14} />
          </button>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              data-testid={testId ? `${testId}-picker` : undefined}
              className="p-1.5 rounded-md text-blue-600 hover:text-blue-800 hover:bg-blue-50"
              aria-label="Abrir calendário"
            >
              <CalendarIcon size={16} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0">
            <Calendar
              mode="single"
              selected={selected || undefined}
              onSelect={(d) => {
                if (d) {
                  onChange(dateToISO(d));
                  setDraft(fmtUS(d));
                }
                setOpen(false);
              }}
              fromDate={minDate || undefined}
              toDate={maxDate || undefined}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

export default DatePickerUS;
